import { useEffect, useMemo, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { isUserDateInputValid, SellerEntity, USER_DATE_YEAR_RANGE_LABEL } from '@crm/shared'
import { previewIntake } from '../../shared/api/requests.api'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { NewClientModal } from '../../shared/ui/NewClientModal'
import type { RequestType, Client } from '../../shared/api/types'
import { ItemsTable, makeEmptyRow } from './components/ItemsTable'
import type { ItemRow } from './components/ItemsTable'
import { getBrands } from '../../shared/api/brands.api'
import { buildKnownBrandSet, isKnownBrand } from './utils/brandMatch'
import { useNewRequestSubmit } from './hooks/useNewRequestSubmit'
import { useAutoSaveDraft } from './hooks/useAutoSaveDraft'
import { buildParsedLines, mapRowsToItems, type ParsedLineStatus } from './utils/mapIntakeResult'
import type { HighlightMode } from './components/ParserStatsPanel'
import { useAuthStore } from '../auth/authStore'
import { NewRequestHeader } from './components/NewRequestHeader'
import { ParsedLinesView } from './components/ParsedLinesView'
import { jumpToSourceLine } from './utils/flashAnchor'
import { isRowFilled } from './utils/rowFilled'
import { useItemRowsEditor } from './hooks/useItemRowsEditor'
import { useDraftLoader } from './hooks/useDraftLoader'
import { useSelectedClient } from './hooks/useSelectedClient'

export function NewRequestPage() {
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()

  // Режим редактирования черновика — id существующей заявки со status=DRAFT.
  // Если задан — форма прогружается из БД, кнопка «Создать заявку» промоутит DRAFT → NEW.
  const draftId = searchParams.get('draftId')

  // Поля шапки
  const [clientId, setClientId] = useState(searchParams.get('clientId') ?? '')
  const [showNewClientModal, setShowNewClientModal] = useState(false)
  const [type, setType] = useState<RequestType>('TEKUCHKA')
  const [sellerEntityId, setSellerEntityId] = useState<SellerEntity>(SellerEntity.SPM)
  const [deadline, setDeadline] = useState('')
  const [notes, setNotes] = useState('')

  // Роль ASSISTANT (Помощник): заявка обязана принадлежать одному из
  // прикреплённых менеджеров — себя помощник выбрать не может.
  const isAssistant = user?.role === 'ASSISTANT'
  const assistantManagers = useMemo(() => user?.assistantManagers ?? [], [user])
  const [ownerManagerId, setOwnerManagerId] = useState('')

  // Текстовый ввод / распознавание
  const [rawText, setRawText] = useState('')
  const [warnings, setWarnings] = useState<string[]>([])

  const [parsedLines, setParsedLines] = useState<ParsedLineStatus[]>([])

  // Строки позиций
  const [items, setItems] = useState<ItemRow[]>([makeEmptyRow()])

  // UID'ы строк, временно подсвеченных в таблице, и режим подсветки —
  // «дубли» или «с ошибками». Кнопки в правой панели статистики переключают
  // режим, поэтому одна подсветка за раз. Локальное UI-состояние, в БД не уходит.
  const [highlightUids, setHighlightUids] = useState<Set<string>>(new Set())
  const [highlightMode, setHighlightMode] = useState<HighlightMode>('none')

  // При активном фильтре в обеих таблицах остаются только подсвеченные строки.
  // Превью отдельно фильтруется по lineNum'ам отобранных items.
  const displayedItems = useMemo(
    () => (highlightMode === 'none' ? items : items.filter((i) => highlightUids.has(i.uid))),
    [items, highlightUids, highlightMode],
  )
  const visibleLineNums = useMemo(() => {
    if (highlightMode === 'none') return null
    const s = new Set<number>()
    for (const i of displayedItems) if (i.lineNumber != null) s.add(i.lineNumber)
    return s
  }, [displayedItems, highlightMode])

  const [submitError, setSubmitError] = useState<unknown>(null)

  // Выбранный клиент целиком: поле «Клиент» и менеджер клиента для помощника.
  const { selectedClient, setPickedClient } = useSelectedClient(clientId)

  const { data: brands } = useQuery({
    queryKey: ['brands'],
    queryFn: getBrands,
  })
  const knownBrands = useMemo(() => buildKnownBrandSet(brands), [brands])

  // Если бренды подгрузились после парсинга — применяем матрицу к уже заполненным строкам
  useEffect(() => {
    if (knownBrands.size === 0) return
    setItems((prev) => {
      let changed = false
      const next = prev.map((row) => {
        if (!row.matrix && row.brand && isKnownBrand(knownBrands, row.brand)) {
          changed = true
          return { ...row, matrix: 'Производители' }
        }
        return row
      })
      return changed ? next : prev
    })
  }, [knownBrands])

  // Загрузка существующего черновика — один раз при монтировании (hooks/useDraftLoader).
  const draftSetters = useMemo(
    () => ({
      setClientId,
      setType,
      setSellerEntityId,
      setDeadline,
      setNotes,
      setOwnerManagerId,
      setRawText,
      setParsedLines,
      setItems,
      setClient: setPickedClient,
    }),
    [setPickedClient],
  )
  const { draftLoaded, setDraftLoaded } = useDraftLoader(draftId, isAssistant, draftSetters)

  // ── Распознавание текста ───────────────────────────────────────────────────

  // Текст распознавания — аргумент мутации, а не значение из замыкания:
  // вставка запускает превью раньше, чем React обновит rawText (см. handlePaste).
  const previewMutation = useMutation({
    mutationFn: (text: string) => previewIntake(text),
    onSuccess: (result, text) => {
      setParsedLines(buildParsedLines(text, result))
      const nextWarnings: string[] = []
      if (result.skipped.length > 0) {
        nextWarnings.push(`Пропущено строк: ${result.skipped.length}`)
      }
      if (result.rows.length > 0) {
        const { items: nextItems, extras } = mapRowsToItems(text, result, knownBrands)
        setItems(nextItems)
        if (extras.length > 0) {
          // Лишние rows от парсера (расщеплённые/синтетические) — в таблицу
          // не идут, их данные показываем здесь, чтобы менеджер не потерял.
          const preview = extras.slice(0, 8).join('; ')
          const tail = extras.length > 8 ? ` … ещё ${extras.length - 8}` : ''
          nextWarnings.push(
            `Парсер вернул ${extras.length} доп. позиций без привязки к строке: ${preview}${tail}`,
          )
        }
      }
      setWarnings(nextWarnings)
    },
  })

  // Автораспознавание при вставке. Текст читаем из самого поля, а не из
  // стейта: onPaste срабатывает ДО того, как React обновит rawText, и
  // замыкание держит значение прошлого рендера. При первой вставке в пустое
  // поле это давало rawText='' — проверка не проходила, распознавание молча
  // не запускалось, и менеджер получал заявку без своих позиций
  // (жалоба 15.09.2026). 80 мс — пауза, за которую браузер успевает вставить
  // текст в textarea.
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget
    setTimeout(() => {
      const text = el.value
      if (text.trim()) previewMutation.mutate(text)
    }, 80)
  }

  // ── Редактирование таблицы позиций (hooks/useItemRowsEditor) ─────────────

  const { updateItem, deleteItem, addRow, reorderItems, clearNotes } = useItemRowsEditor(
    setItems,
    displayedItems,
  )

  const clearAll = () => {
    setItems([makeEmptyRow()])
    setRawText('')
    setWarnings([])
    setParsedLines([])
  }

  // Дефолт менеджера-владельца для помощника: владелец выбранного клиента
  // (если он среди прикреплённых), иначе первый прикреплённый менеджер.
  useEffect(() => {
    if (!isAssistant || ownerManagerId || assistantManagers.length === 0) return
    const clientOwner = selectedClient?.managerId
    const fallback = assistantManagers[0]?.id ?? ''
    setOwnerManagerId(
      clientOwner && assistantManagers.some((m) => m.id === clientOwner) ? clientOwner : fallback,
    )
  }, [isAssistant, ownerManagerId, assistantManagers, selectedClient?.managerId])

  // ── Отправка ──────────────────────────────────────────────────────────────

  // Год дедлайна проверяется сразу, до отправки: нативный date-input пускает
  // 62026 или 1999, а сервер такие даты отклоняет (№137, пакет 5).
  const deadlineError =
    deadline && !isUserDateInputValid(deadline)
      ? `Год дедлайна вне допустимого диапазона (${USER_DATE_YEAR_RANGE_LABEL}) — проверьте дату`
      : null

  // Пустая строка-заготовка (форма всегда держит одну, и она отмечена) в БД
  // не уезжает: иначе автосейв и «Создать заявку» записывали её позицией
  // «??? / 1 pcs / без бренда» вместо позиций менеджера (жалоба 15.09.2026).
  // См. isRowFilled в utils/rowFilled.ts.
  const filledItems = useMemo(() => items.filter(isRowFilled), [items])
  // Отправляем ВСЕ отмеченные заполненные строки — даже с нераспознанным
  // партномером: битые попадут в заявку и будут подсвечены красным.
  const selectedItems = useMemo(() => filledItems.filter((i) => i.checked), [filledItems])
  const canSubmit =
    clientId &&
    selectedItems.length > 0 &&
    deadline &&
    !deadlineError &&
    (!isAssistant || ownerManagerId)
  // Черновик — минимальные требования: выбран клиент (помощнику — ещё менеджер).
  const canSaveDraft = !!clientId && (!isAssistant || !!ownerManagerId)

  // Метаданные парсинга — сохраняем только если текст непустой или есть
  // распознанные строки. Для новой заявки без вставки получится null —
  // бэкенд обновит поле в значение null (очистит).
  const intakeMetaPayload = useMemo(() => {
    if (!rawText.trim() && parsedLines.length === 0) return null
    return { rawText, parsedLines }
  }, [rawText, parsedLines])

  const { saveDraftMutation, createMutation, autoSave, isPending } = useNewRequestSubmit({
    clientId,
    type,
    sellerEntityId,
    deadline,
    notes,
    allItems: filledItems,
    selectedItems,
    draftId,
    intakeMeta: intakeMetaPayload,
    ownerManagerId: isAssistant ? ownerManagerId : null,
    onError: setSubmitError,
  })

  // Автосохранение в DRAFT с debounce 2 сек.
  // enabled=true когда:
  //  - новая заявка (нет draftId) — можно сразу сохранять
  //  - или черновик уже загружен из БД (draftLoaded=true), чтобы не перезаписать форму пустотой
  const autoSaveEnabled = !draftId || draftLoaded
  const { lastSavedAt, isSaving: isAutoSaving } = useAutoSaveDraft({
    clientId,
    type,
    sellerEntityId,
    deadline,
    notes,
    items: selectedItems,
    intakeMeta: intakeMetaPayload,
    urlDraftId: draftId,
    enabled: autoSaveEnabled,
    save: autoSave,
    onSaved: () => {
      // После первого автосейва новый draftId появится в URL, useQuery его
      // подхватит — выставим draftLoaded=true, чтобы перезагрузка формы
      // свежими данными не перетёрла пользовательский ввод.
      if (!draftLoaded) setDraftLoaded(true)
    },
  })

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3">
        <Link to="/requests" className="text-gray-400 hover:text-gray-600 text-sm">
          ← Заявки
        </Link>
        <h1 className="text-xl font-semibold text-gray-900">
          {draftId ? 'Черновик заявки' : 'Новая заявка'}
        </h1>
        {draftId && (
          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
            DRAFT
          </span>
        )}
      </div>

      <NewRequestHeader
        isAssistant={isAssistant}
        ownerManagerId={ownerManagerId}
        assistantManagers={assistantManagers}
        onOwnerChange={setOwnerManagerId}
        clientId={clientId}
        selectedClient={selectedClient}
        onClientChange={(client) => {
          setClientId(client.id)
          setPickedClient(client)
        }}
        onNewClient={() => setShowNewClientModal(true)}
        type={type}
        onTypeChange={setType}
        sellerEntityId={sellerEntityId}
        onSellerEntityChange={setSellerEntityId}
        deadline={deadline}
        deadlineError={deadlineError}
        onDeadlineChange={(v) => {
          setDeadline(v)
          // Ошибка прошлой отправки (например, про год дедлайна) после правки
          // даты уже неактуальна — иначе блок внизу висел бы до ухода со страницы.
          setSubmitError(null)
        }}
        notes={notes}
        onNotesChange={setNotes}
      />

      {/* Вставка текста / результат распознавания */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-700">
            {parsedLines.length > 0 ? 'Результат распознавания' : 'Вставьте список позиций'}
          </h2>
          {parsedLines.length > 0 ? (
            <button
              type="button"
              onClick={() => setParsedLines([])}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              ← Изменить текст
            </button>
          ) : (
            <span className="text-xs text-gray-400">или вставьте — распознается автоматически</span>
          )}
        </div>

        {parsedLines.length > 0 ? (
          <ParsedLinesView
            parsedLines={parsedLines}
            visibleLineNums={visibleLineNums}
            items={items}
            highlightMode={highlightMode}
            onHighlight={(mode, uids) => {
              setHighlightMode(mode)
              setHighlightUids(uids)
            }}
            onClearHighlight={() => {
              setHighlightMode('none')
              setHighlightUids(new Set())
            }}
          />
        ) : (
          <>
            <textarea
              value={rawText}
              onChange={(e) => {
                setRawText(e.target.value)
                previewMutation.reset()
              }}
              onPaste={handlePaste}
              rows={12}
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y min-h-[240px]"
              placeholder={
                'Вставьте из email, Excel или WhatsApp:\n' +
                'STM32F103C8T6\tST\t100\n' +
                'LM358N 50 шт // срочно\n' +
                'BC547 200 ea'
              }
            />
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => previewMutation.mutate(rawText)}
                disabled={!rawText.trim() || previewMutation.isPending}
                className="px-4 py-1.5 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-900 disabled:opacity-50 transition-colors"
              >
                {previewMutation.isPending ? 'Обработка...' : 'Распознать'}
              </button>
            </div>
          </>
        )}

        {previewMutation.isError && <ErrorMessage error={previewMutation.error} />}
      </div>

      {/* Предупреждения парсера */}
      {warnings.length > 0 && (
        <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
          {warnings.join(' · ')}
        </div>
      )}

      {/* Таблица позиций */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-700">
            Позиции
            {selectedItems.length > 0 && (
              <span className="ml-1.5 text-xs text-gray-400">({selectedItems.length} выбрано)</span>
            )}
          </h2>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={clearNotes}
              className="text-xs text-gray-400 hover:text-gray-700"
              title="Стереть содержимое колонки «Заметка» у всех позиций"
            >
              Очистить заметки
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-gray-400 hover:text-red-500"
            >
              Очистить всё
            </button>
          </div>
        </div>

        <ItemsTable
          items={displayedItems}
          onChange={updateItem}
          onDelete={deleteItem}
          onAdd={addRow}
          onClearAll={clearAll}
          // DnD по отфильтрованной таблице создал бы непонятный порядок —
          // отключаем reorder, пока фильтр активен.
          onReorder={highlightMode === 'none' ? reorderItems : undefined}
          knownBrands={knownBrands}
          onJumpToSourceLine={jumpToSourceLine}
          highlightUids={highlightUids}
          highlightMode={highlightMode}
        />
      </div>

      {/* Ошибка */}
      {submitError != null && <ErrorMessage error={submitError} />}

      {/* Кнопки отправки */}
      <div className="flex gap-3 items-center flex-wrap">
        <button
          type="button"
          onClick={() => createMutation.mutate()}
          disabled={!canSubmit || isPending || isAutoSaving}
          className="px-5 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
        >
          {createMutation.isPending || isAutoSaving ? 'Создание...' : 'Создать заявку'}
        </button>
        <button
          type="button"
          onClick={() => saveDraftMutation.mutate()}
          disabled={!canSaveDraft || isPending}
          title="Сохранить как черновик — позже можно дооформить и превратить в заявку"
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          {saveDraftMutation.isPending ? 'Сохранение...' : 'Сохранить черновик'}
        </button>
        <Link
          to="/requests"
          className="px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
        >
          Отмена
        </Link>
        {clientId && selectedItems.length > 0 && !deadline && (
          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
            Для создания заявки укажите дату дедлайна
          </span>
        )}
        {clientId && selectedItems.length === 0 && (
          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
            {rawText.trim()
              ? 'Нажмите «Распознать» — позиции появятся в таблице ниже'
              : 'Заполните хотя бы одну позицию — вставьте список или введите артикул вручную'}
          </span>
        )}
        {/* Индикатор автосохранения (справа, ненавязчиво) */}
        {clientId && (
          <span className="text-xs text-gray-500 ml-auto">
            {isAutoSaving
              ? 'Сохранение черновика…'
              : lastSavedAt
                ? `Черновик сохранён в ${lastSavedAt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
                : 'Автосохранение активно'}
          </span>
        )}
      </div>

      {showNewClientModal && (
        <NewClientModal
          onCreated={(client: Client) => {
            // Клиента кладём целиком: в первой странице выпадашки его может не
            // быть, и поле выглядело бы пустым (проверка Сергея 15.09.2026).
            setClientId(client.id)
            setPickedClient(client)
            setShowNewClientModal(false)
          }}
          onClose={() => setShowNewClientModal(false)}
        />
      )}
    </div>
  )
}
